import React, { useState, FC } from "react";
import { ProjectDataType, ShipDataType, StudyDataType } from "@/types/apiTypes";
import { notifications } from "@mantine/notifications";
import { GenShips } from "@/components/GenShips/GenShips";
import { GenProjects } from "@/components/GenProjects/GenProjects";
import { GenStudies } from "@/components/GenStudies/GenStudies";
import { useDeleteShip } from "@/hooks/apiHooks/useDeleteShip";
import { useDeleteStudy } from "@/hooks/apiHooks/useDeleteStudy";
import useCreateProject from "../hooks/apiHooks/useCreateProject";
import useEditProject from "../hooks/apiHooks/useEditProject";
import { useInvalidateQueries } from "@/hooks/helperHooks/useInvalidateQueries";
import { shipIsInProject, studyIsInProject } from "@/helpers/isInProject";
import { useAssociateShipToProject } from "@/hooks/apiHooks/useAssociateShipToProject";
import { useAssociateStudyToProject } from "@/hooks/apiHooks/useAssociateStudyToProject";
import { ModalA, ModalB, ModalC, ModalD } from "@/components/Modals/Modals";
import styles from "../css/home.module.css";

const Home: FC = () => {
  const { invalidateProjects } = useInvalidateQueries();

  const [selectedProject, setSelectedProject] =
    useState<ProjectDataType | null>(null);

  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState(false);

  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);

  const [editProjectData, setEditProjectData] =
    useState<ProjectDataType | null>(null);

  const [projectDeadline, setProjectDeadline] = useState<Date | null>(null);

  const [isShipProjectSelectModalOpen, setIsShipProjectSelectModalOpen] =
    useState(false);

  const [isStudyProjectSelectModalOpen, setIsStudyProjectSelectModalOpen] =
    useState(false);

  const [shipToAssociate, setShipToAssociate] = useState<ShipDataType | null>(
    null,
  );

  const [projectToAssociateId, setProjectToAssociateId] = useState<
    string | null
  >(null);

  const [studyToAssociate, setStudyToAssociate] =
    useState<StudyDataType | null>(null);

  const {
    mutate: createProjectFunction,
    //isError: createProjectIsError,
    // error: createProjectError,
  } = useCreateProject();

  const {
    mutate: editProjectFunction,
    // isError: editProjectIsError,
    // error: editProjectError,
  } = useEditProject();

  const {
    deleteShip,
    // handleDeleteShip,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    deleteShipId,
    setDeleteShipId,
    associatedStudies,
    // setAssociatedStudies,
    isAssociatedStudiesModalOpen,
    setIsAssociatedStudiesModalOpen,
  } = useDeleteShip();

  const {
    confirmDeleteStudy,
    isDeleteStudyModalOpen,
    setIsDeleteStudyModalOpen,
    deleteStudyId,
    setDeleteStudyId,
  } = useDeleteStudy();

  const openEditProjectModal = (project: ProjectDataType) => {
    setEditProjectData(project);
    setNewProjectName(project.name);
    setNewProjectDescription(project.description || "");
    setProjectDeadline(project.deadline ? new Date(project.deadline) : null);
    setIsEditProjectModalOpen(true);
  };

  const handleProjectClick = (project: ProjectDataType | null) => {
    setSelectedProject(project);
  };

  const { mutate: associateShipFunction } = useAssociateShipToProject();
  const { mutate: associateStudyFunction } = useAssociateStudyToProject();

  const createProject = async () => {
    createProjectFunction({
      name: newProjectName,
      description: newProjectDescription,
      deadline: projectDeadline,
    });
    setIsCreateProjectModalOpen(false);
    setNewProjectName("");
    setNewProjectDescription("");
    setProjectDeadline(null);
    // INVALIDATE PROJECTS QUERY
    invalidateProjects();
  };

  const handleEditProject = async () => {
    editProjectFunction({
      id: editProjectData?.id ?? null,
      name: newProjectName,
      description: newProjectDescription,
      deadline: projectDeadline
        ? projectDeadline.toISOString().split("T")[0]
        : null,
    });
    setIsEditProjectModalOpen(false);
    setEditProjectData(null);
    setNewProjectName("");
    setNewProjectDescription("");
    setProjectDeadline(null);
    // INVALIDATE PROJECTS QUERY
    invalidateProjects();
  };

  const associateShipToProject = (
    ship: ShipDataType,
    projectId: string,
  ): void => {
    associateShipFunction({ ship, projectId });
  };

  const associateStudyToProject = (
    study: StudyDataType,
    projectId: string,
  ): void => {
    associateStudyFunction({ study, projectId });
  };

  const handleShipProjectAssociation = (ship: ShipDataType) => {
    setShipToAssociate(ship);
    setIsShipProjectSelectModalOpen(true);
    // associateShipAndProject(ship);
  };

  const handleStudyProjectAssociation = (study: StudyDataType) => {
    setStudyToAssociate(study);
    setIsStudyProjectSelectModalOpen(true);
    // associateStudyAndProject(study);
  };

  const confirmDeleteShip = (shipId: string) => {
    deleteShip(shipId).then(() => console.log("Delete ship"));
  };

  return (
    <div
      style={{
        // backgroundColor: "orange",
        overflow: "visible",
        pointerEvents: "auto",
        position: "relative",
        zIndex: "auto",
        maxWidth: "100%",
      }}
    >
      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}

      <GenProjects
        selectedProject={selectedProject}
        handleProjectClick={handleProjectClick}
        openEditProjectModal={openEditProjectModal}
        setIsCreateProjectModalOpen={setIsCreateProjectModalOpen}
      />
      <div className={styles.container}>
        {/*}// ---------------------------------- // */}
        {/*}// ---------------------------------- // */}
        {/*}// ---------------------------------- // */}

        <div>
          <GenShips
            setDeleteShipId={setDeleteShipId}
            selectedProject={selectedProject}
            setIsDeleteModalOpen={setIsDeleteModalOpen}
            confirmDeleteShip={confirmDeleteShip}
            shipIsInProject={shipIsInProject}
            handleShipProjectAssociation={handleShipProjectAssociation}
            setIsAssociatedStudiesModalOpen={setIsAssociatedStudiesModalOpen}
          />
        </div>

        {/*}// ---------------------------------- // */}
        {/*}// ---------------------------------- // */}
        {/*}// ---------------------------------- // */}

        <div>
          <GenStudies
            selectedProject={selectedProject}
            setDeleteStudyId={setDeleteStudyId}
            setIsDeleteStudyModalOpen={setIsDeleteStudyModalOpen}
            studyIsInProject={studyIsInProject}
            handleStudyProjectAssociation={handleStudyProjectAssociation}
          />
        </div>
      </div>

      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}

      <ModalA
        isOpen={isDeleteStudyModalOpen}
        onClose={setIsDeleteStudyModalOpen}
        title="Confirm Deletion"
        message="Are you sure you want to delete this study? This action cannot be
            undone."
        btn1Title="Cancel"
        btn2Title="Delete"
        btn1Action={() => setIsDeleteStudyModalOpen(false)}
        btn2Action={() => deleteStudyId && confirmDeleteStudy(deleteStudyId)}
      />

      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}

      <ModalA
        isOpen={isDeleteModalOpen}
        onClose={setIsDeleteModalOpen}
        title="Confirm Deletion"
        message="Are you sure you want to delete this ship? This action cannot
            be undone."
        btn1Title="Cancel"
        btn2Title="Delete"
        btn1Action={() => setIsDeleteModalOpen(false)}
        btn2Action={() => deleteShipId && confirmDeleteShip(deleteShipId)}
      />

      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}

      <ModalD
        isOpen={isAssociatedStudiesModalOpen}
        onClose={setIsAssociatedStudiesModalOpen}
        title="Cannot Delete Ship"
        associatedStudies={associatedStudies}
        btnOnClick={setIsAssociatedStudiesModalOpen}
      />

      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}

      <ModalB
        isOpen={isCreateProjectModalOpen}
        onClose={setIsCreateProjectModalOpen}
        title="Create New Project"
        input1Name="Project Name"
        input1Value={newProjectName}
        setInput1Value={setNewProjectName}
        input2Name={"Description"}
        input2Value={newProjectDescription}
        setInput2Value={setNewProjectDescription}
        datePickerPlaceholder={"Select deadline"}
        datePickerValue={projectDeadline}
        setDatePickerValue={setProjectDeadline}
        datePickerLabel="Date input"
        btn1Title="Cancel"
        btn2Title="Create"
        btn1Action={() => setIsCreateProjectModalOpen(false)}
        btn2Action={createProject}
      />

      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}

      <ModalB
        isOpen={isEditProjectModalOpen}
        onClose={setIsEditProjectModalOpen}
        title="Edit Project"
        input1Name="Project Name"
        input1Value={newProjectName}
        setInput1Value={setNewProjectName}
        input2Name="Description"
        input2Value={newProjectDescription}
        setInput2Value={setNewProjectDescription}
        datePickerPlaceholder={"Select deadline"}
        datePickerValue={projectDeadline}
        setDatePickerValue={setProjectDeadline}
        datePickerLabel="Date input"
        btn1Title="Cancel"
        btn2Title="Save Changes"
        btn1Action={() => setIsEditProjectModalOpen(false)}
        btn2Action={handleEditProject}
      />

      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}

      <ModalC
        isOpen={isShipProjectSelectModalOpen}
        onClose={setIsShipProjectSelectModalOpen}
        title="Select Project"
        selectLabel="Project"
        selectPlaceholder="Select a project"
        selectValue={projectToAssociateId}
        setSelectValue={setProjectToAssociateId}
        btnLabel="Add to Project"
        btnOnClick={() => {
          if (shipToAssociate && projectToAssociateId) {
            associateShipToProject(shipToAssociate, projectToAssociateId);
            setIsShipProjectSelectModalOpen(false);
            setShipToAssociate(null);
            setProjectToAssociateId(null);
          } else {
            console.log(shipToAssociate);
            console.log(projectToAssociateId);

            notifications.show({
              title: "Error",
              message: "Please select a project.",
              color: "error",
            });
          }
        }}
      />

      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}
      {/*}// ---------------------------------- // */}

      <ModalC
        isOpen={isStudyProjectSelectModalOpen}
        onClose={setIsStudyProjectSelectModalOpen}
        title="Select Project"
        selectLabel="Project"
        selectPlaceholder="Select a project"
        selectValue={projectToAssociateId}
        setSelectValue={setProjectToAssociateId}
        btnLabel="Add to Project"
        btnOnClick={() => {
          if (studyToAssociate && projectToAssociateId) {
            associateStudyToProject(studyToAssociate, projectToAssociateId);
            setIsStudyProjectSelectModalOpen(false);
            setStudyToAssociate(null);
            setProjectToAssociateId(null);
          } else {
            notifications.show({
              title: "Error",
              message: "Please select a project.",
              color: "error",
            });
          }
        }}
      />
    </div>
  );
};

export default Home;
